import { useState } from 'react';
import {
  FACT_KEY_NEEDS,
  FACT_KEY_PRODUCTS,
  NEED_SITUATIONS,
  PROPERTY_CATEGORIES,
  SUBTYPES_BY_CATEGORY,
  TRANSACTION_TYPES,
  type FactKeyNeed,
  type FactKeyProduct,
  type NeedSituation,
  type PropertyCategory,
  type PropertySubtype,
  type TransactionType,
} from '@immo/api-contract';
import { Button, InfoBox, NativeSelect } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import {
  conditionsLabel,
  nextNeedRowId,
  sortNeedRows,
  type DraftNeedRow,
  type NeedConflict,
} from '@/pages/skills/fact-key-needs';

const ALL = '';
const ROW_GRID = 'grid grid-cols-[repeat(5,minmax(0,1fr))_2.5rem] items-center gap-2 px-3';
const NEED_CLASSES: Record<FactKeyNeed, string> = {
  REQUIRED: 'font-semibold text-text-primary',
  RECOMMENDED: 'font-medium text-text-primary',
  OPTIONAL: 'text-text-secondary',
  NOT_USED: 'text-text-tertiary',
};

type Props = {
  readonly categories: ReadonlyArray<PropertyCategory>;
  readonly rows: ReadonlyArray<DraftNeedRow>;
  readonly onChange: (rows: ReadonlyArray<DraftNeedRow>) => void;
  readonly readOnly?: boolean;
  readonly conflict?: NeedConflict | null;
};

export function FactKeyNeedRows({ categories, rows, onChange, readOnly = false, conflict = null }: Props) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tNeeds = useTranslations('pages.admin.skills.fact-keys.needs');
  const tCategory = useTranslations('common.immobilientyp.category');
  const tSubtype = useTranslations('common.immobilientyp.subtype');
  const [openIds, setOpenIds] = useState<ReadonlySet<number>>(new Set());
  const ticked = PROPERTY_CATEGORIES.filter((category) => categories.includes(category));

  if (ticked.length === 0) return null;

  const labels = {
    marketingType: (value: TransactionType) => tNeeds(`marketing-type.${value}`),
    subtype: (value: PropertySubtype) => tSubtype(value),
    situation: (value: NeedSituation) => tNeeds(`situation.${value}`),
  };
  const open = (id: number) => setOpenIds(new Set([...openIds, id]));
  const update = (id: number, changes: Partial<DraftNeedRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  const add = (product: FactKeyProduct) => {
    const id = nextNeedRowId();

    open(id);
    onChange([
      ...rows,
      {
        id,
        product,
        category: ticked[0] ?? 'APARTMENT',
        marketingType: null,
        subtype: null,
        situation: null,
        need: 'NOT_USED',
      },
    ]);
  };

  return (
    <div className="overflow-hidden rounded-md border border-border text-sm">
      <div className={`${ROW_GRID} h-9 bg-subtle text-xs text-text-tertiary`}>
        <span>{tNeeds('columns.category')}</span>
        <span>{tNeeds('columns.marketing-type')}</span>
        <span>{tNeeds('columns.subtype')}</span>
        <span>{tNeeds('columns.situation')}</span>
        <span>{tNeeds('columns.need')}</span>
        <span />
      </div>
      {FACT_KEY_PRODUCTS.map((product) => {
        const productRows = sortNeedRows(rows.filter((row) => row.product === product));
        const productName = t(`product.${product}`);

        return (
          <section key={product}>
            <div className="flex h-9 items-center justify-between bg-canvas px-3">
              <h4 className="text-base-sm font-semibold text-text-primary">{productName}</h4>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={tNeeds('product-control', { control: tNeeds('add-row'), product: productName })}
                  onClick={() => add(product)}
                >
                  <PlusIcon />
                </Button>
              )}
            </div>
            {productRows.map((row, index) => {
              const previous = productRows[index - 1];
              const firstOfCategory = previous?.category !== row.category;
              const border =
                index === 0 ? '' : firstOfCategory ? 'border-t border-border' : 'border-t border-dashed border-border';
              const isEditing = !readOnly && (openIds.has(row.id) || conflict?.offendingId === row.id);
              const offending = conflict?.offendingId === row.id;
              const control = (name: string) =>
                tNeeds('row-control', { control: name, product: productName, position: index + 1 });
              const selectOptions = (all: string, tokens: ReadonlyArray<{ value: string; label: string }>) => [
                { value: ALL, label: all },
                ...tokens,
              ];

              return (
                <div key={row.id} className={border}>
                  {isEditing ? (
                    <div className={`${ROW_GRID} min-h-9 bg-selected-bg py-1`}>
                      <NativeSelect
                        size="sm"
                        invalid={offending}
                        aria-label={control(tNeeds('columns.category'))}
                        value={row.category}
                        onChange={(value) => {
                          const category = value as PropertyCategory;
                          const keeps =
                            row.subtype === null ||
                            (SUBTYPES_BY_CATEGORY[category] as readonly string[]).includes(row.subtype);

                          update(row.id, { category, subtype: keeps ? row.subtype : null });
                        }}
                        options={ticked.map((category) => ({
                          value: category,
                          label: tCategory(category),
                        }))}
                      />
                      <NativeSelect
                        size="sm"
                        invalid={offending}
                        aria-label={control(tNeeds('columns.marketing-type'))}
                        value={row.marketingType ?? ALL}
                        onChange={(value) =>
                          update(row.id, { marketingType: value === ALL ? null : (value as TransactionType) })
                        }
                        options={selectOptions(
                          tNeeds('marketing-type.ALL'),
                          TRANSACTION_TYPES.map((type) => ({ value: type, label: tNeeds(`marketing-type.${type}`) })),
                        )}
                      />
                      <NativeSelect
                        size="sm"
                        invalid={offending}
                        aria-label={control(tNeeds('columns.subtype'))}
                        value={row.subtype ?? ALL}
                        onChange={(value) =>
                          update(row.id, { subtype: value === ALL ? null : (value as PropertySubtype) })
                        }
                        options={selectOptions(
                          tNeeds('subtype.ALL'),
                          SUBTYPES_BY_CATEGORY[row.category].map((subtype) => ({
                            value: subtype,
                            label: tSubtype(subtype),
                          })),
                        )}
                      />
                      <NativeSelect
                        size="sm"
                        invalid={offending}
                        aria-label={control(tNeeds('columns.situation'))}
                        value={row.situation ?? ALL}
                        onChange={(value) =>
                          update(row.id, { situation: value === ALL ? null : (value as NeedSituation) })
                        }
                        options={selectOptions(
                          tNeeds('situation.ALL'),
                          NEED_SITUATIONS.map((situation) => ({
                            value: situation,
                            label: tNeeds(`situation.${situation}`),
                          })),
                        )}
                      />
                      <NativeSelect
                        size="sm"
                        invalid={offending}
                        aria-label={control(tNeeds('columns.need'))}
                        value={row.need}
                        onChange={(value) => update(row.id, { need: value as FactKeyNeed })}
                        options={FACT_KEY_NEEDS.map((need) => ({ value: need, label: t(`need.${need}`) }))}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={control(tNeeds('remove-row'))}
                        onClick={() => onChange(rows.filter((each) => each.id !== row.id))}
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  ) : (
                    <ReadRow
                      row={row}
                      showCategory={firstOfCategory}
                      tinted={conflict?.partner.id === row.id}
                      interactive={!readOnly}
                      onOpen={() => open(row.id)}
                    />
                  )}
                  {offending && conflict !== null && (
                    <div className="px-3 pb-2">
                      <InfoBox type="danger">
                        {tNeeds('conflict', {
                          conditions: conditionsLabel(conflict.partner, labels) || tNeeds('marketing-type.ALL'),
                          need: t(`need.${conflict.partner.need}`),
                        })}
                      </InfoBox>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

type ReadRowProps = {
  readonly row: DraftNeedRow;
  readonly showCategory: boolean;
  readonly tinted: boolean;
  readonly interactive: boolean;
  readonly onOpen: () => void;
};

function ReadRow({ row, showCategory, tinted, interactive, onOpen }: ReadRowProps) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tNeeds = useTranslations('pages.admin.skills.fact-keys.needs');
  const tCategory = useTranslations('common.immobilientyp.category');
  const tSubtype = useTranslations('common.immobilientyp.subtype');
  const all = <span className="text-text-tertiary">{tNeeds('marketing-type.ALL')}</span>;
  const classes = `${ROW_GRID} h-9 w-full text-left ${tinted ? 'bg-danger-50' : ''} ${interactive ? 'hover:bg-hover-bg' : ''}`;
  const content = (
    <>
      <span className="text-text-primary">{showCategory ? tCategory(row.category) : ''}</span>
      <span>{row.marketingType === null ? all : tNeeds(`marketing-type.${row.marketingType}`)}</span>
      <span>{row.subtype === null ? all : tSubtype(row.subtype)}</span>
      <span>{row.situation === null ? all : tNeeds(`situation.${row.situation}`)}</span>
      <span className={NEED_CLASSES[row.need]}>{t(`need.${row.need}`)}</span>
      <span />
    </>
  );

  return interactive ? (
    <button type="button" className={classes} onClick={onOpen}>
      {content}
    </button>
  ) : (
    <div className={classes}>{content}</div>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}
